import { Button, ButtonGroup, DropdownMenu, DropdownMenuCheckIcon } from '@components'
import type { IDashboardFilter, IDateRangeKey, InteractiveReportKey } from '@query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowRight, FaCaretDown, FaPen, FaPlus, FaTrashAlt } from 'react-icons/fa'
import {
  AddColumnButton,
  ColumnHeaderCell,
  ColumnHeaderValueSlot,
  ValueHeader
} from './interactive.styled'
import {
  hasChangeColumns,
  MAX_PERIOD_COLUMNS,
  ACCOUNTING_VALUE_COLUMN_WIDTH,
  DRILL_COLUMN_VALUE_INDEX,
  type PeriodColumn
} from './periodUtils'

type Props = {
  column: PeriodColumn
  label: string
  periodFilters: IDashboardFilter[]
  canInsert: boolean
  canRemove: boolean
  highlighted?: boolean
  isStale?: boolean
  onPresetSelect: (preset: IDateRangeKey) => void
  onInsert: (direction: 'before' | 'after') => void
  onRename: () => void
  onRemove: () => void
}

const ColumnHeaderMenu: React.FC<Props> = ({
  column,
  label,
  periodFilters,
  canInsert,
  canRemove,
  highlighted = false,
  isStale = false,
  onPresetSelect,
  onInsert,
  onRename,
  onRemove
}) => {
  const [t] = useTranslation()
  const insertHint = t('reports.interactive.column.maxColumnsHint')
  const removeHint = t('reports.interactive.column.minColumnsHint')

  const menuItems = useMemo(
    () => [
      <ButtonGroup key="presets">
        {periodFilters.map(filter => (
          <Button
            key={filter.key}
            type="button"
            icon={<DropdownMenuCheckIcon selected={column.preset === filter.key} />}
            onClick={() => onPresetSelect(filter.key)}
          >
            {t(`components.dateRangeFilter.${filter.key}`)}
          </Button>
        ))}
      </ButtonGroup>,
      <ButtonGroup key="insert">
        <Button
          type="button"
          icon={<FaArrowLeft />}
          disabled={!canInsert}
          title={!canInsert ? insertHint : undefined}
          onClick={() => onInsert('before')}
        >
          {t('reports.interactive.column.insertBefore')}
        </Button>
        <Button
          type="button"
          icon={<FaArrowRight />}
          disabled={!canInsert}
          title={!canInsert ? insertHint : undefined}
          onClick={() => onInsert('after')}
        >
          {t('reports.interactive.column.insertAfter')}
        </Button>
      </ButtonGroup>,
      <ButtonGroup key="edit">
        <Button type="button" icon={<FaPen />} onClick={onRename}>
          {t('reports.interactive.column.rename')}
        </Button>
        <Button
          type="button"
          intent="danger"
          icon={<FaTrashAlt />}
          disabled={!canRemove}
          title={!canRemove ? removeHint : undefined}
          onClick={onRemove}
        >
          {t('reports.interactive.column.remove')}
        </Button>
      </ButtonGroup>
    ],
    [
      canInsert,
      canRemove,
      column.preset,
      insertHint,
      onInsert,
      onPresetSelect,
      onRemove,
      onRename,
      periodFilters,
      removeHint,
      t
    ]
  )

  return (
    <ColumnHeaderCell $width={ACCOUNTING_VALUE_COLUMN_WIDTH} $highlighted={highlighted}>
      <ColumnHeaderValueSlot>
        <DropdownMenu items={menuItems}>
          <Button
            type="button"
            isSecondary
            size="sm"
            iconRight={<FaCaretDown />}
            showSpinner={isStale}
            title={label}
          >
            {label}
          </Button>
        </DropdownMenu>
      </ColumnHeaderValueSlot>
    </ColumnHeaderCell>
  )
}

export type ColumnControls = {
  reportKey: InteractiveReportKey
  columns: PeriodColumn[]
  periodFilters: IDashboardFilter[]
  onPresetSelect: (columnId: string, preset: IDateRangeKey) => void
  onInsert: (columnId: string, direction: 'before' | 'after') => void
  onAppend: () => void
  onRename: (columnId: string) => void
  onRemove: (columnId: string) => void
}

/**
 * Renders one header per response label. With comparison on, the backend
 * interleaves Change columns, so only every other label maps to a period column
 * and gets a menu.
 */
export const PeriodColumnHeaders: React.FC<{
  controls: ColumnControls
  labels: string[]
  drillColumnHighlighted?: boolean
  isStale?: boolean
}> = ({ controls, labels, drillColumnHighlighted = false, isStale = false }) => {
  const [t] = useTranslation()
  const { columns, periodFilters } = controls
  const withChange = hasChangeColumns(columns.length, labels.length)
  const canInsert = columns.length < MAX_PERIOD_COLUMNS
  const canRemove = columns.length > 1

  return (
    <>
      {labels.map((label, index) => {
        const columnIndex = withChange ? index / 2 : index
        const column = Number.isInteger(columnIndex) ? columns[columnIndex] : undefined

        if (!column) {
          return (
            <ValueHeader key={`change-${index}`} $width={ACCOUNTING_VALUE_COLUMN_WIDTH}>
              {label}
            </ValueHeader>
          )
        }

        return (
          <ColumnHeaderMenu
            key={column.id}
            column={column}
            label={label}
            periodFilters={periodFilters}
            canInsert={canInsert}
            canRemove={canRemove}
            highlighted={drillColumnHighlighted && index === DRILL_COLUMN_VALUE_INDEX}
            isStale={isStale}
            onPresetSelect={preset => controls.onPresetSelect(column.id, preset)}
            onInsert={direction => controls.onInsert(column.id, direction)}
            onRename={() => controls.onRename(column.id)}
            onRemove={() => controls.onRemove(column.id)}
          />
        )
      })}
      <AddColumnButton
        type="button"
        disabled={!canInsert}
        onClick={controls.onAppend}
        title={
          canInsert
            ? t('reports.interactive.column.addColumn')
            : t('reports.interactive.column.maxColumnsHint')
        }
      >
        <FaPlus />
      </AddColumnButton>
    </>
  )
}
